'use client';

import { datadogRum } from '@datadog/browser-rum';
import type { LDClient, LDContext } from 'launchdarkly-react-client-sdk';
import {
	type ComponentType,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react';
import { Spinner } from 'reactstrap';
import { AuthContext } from 'src/context/auth-context/auth-context';
import { getLaunchDarklyContext } from 'src/lib/launchdarkly-context';

const Loading = () => (
	<div
		style={{
			position: 'fixed',
			inset: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: '#fff',
			zIndex: 9999,
		}}
	>
		<Spinner color="primary" />
	</div>
);

export default function LaunchDarklyProvider({
	children,
	context,
}: {
	children: ReactNode;
	context?: LDContext;
}) {
	// Identity changes remount this boundary; attribute updates use the identify hook.
	const [initialContext] = useState(context);
	const [state, setState] = useState<{
		ready: boolean;
		Provider: ComponentType<{ children: ReactNode }> | null;
	}>({ ready: false, Provider: null });

	useEffect(() => {
		let cancelled = false;
		let client: Pick<LDClient, 'close'> | undefined;
		const clientSideID = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID;
		if (!clientSideID) {
			setState({ ready: true, Provider: null });
			return;
		}

		void import('launchdarkly-react-client-sdk')
			.then(async ({ asyncWithLDProvider }) => {
				if (cancelled) return;
				const Provider = await asyncWithLDProvider({
					clientSideID,
					context: initialContext,
					reactOptions: { useCamelCaseFlagKeys: false },
					options: {
						// Capture the SDK-owned client even while initialization is pending.
						plugins: [
							{
								getMetadata: () => ({ name: 'customer-client-lifecycle' }),
								register: (ldClient) => {
									client = ldClient;
									if (cancelled) client.close();
								},
							},
						],
						inspectors: [
							{
								type: 'flag-used',
								name: 'dd-inspector',
								method: (flagKey: string, flagDetail: { value: unknown }) => {
									datadogRum.addFeatureFlagEvaluation(
										flagKey,
										flagDetail.value,
									);
								},
							},
						],
					},
				});
				if (!cancelled) setState({ ready: true, Provider });
			})
			.catch((error) => {
				if (cancelled) return;
				client?.close();
				// biome-ignore lint/suspicious/noConsole: necessary logging
				console.error('Failed to initialize LaunchDarkly:', error);
				setState({ ready: true, Provider: null });
			});

		return () => {
			cancelled = true;
			client?.close();
		};
	}, [initialContext]);

	if (!state.ready) return <Loading />;
	return state.Provider ? (
		<state.Provider>{children}</state.Provider>
	) : (
		children
	);
}

export function PortalLaunchDarklyProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [userDetails, , fetching] = useContext(AuthContext);
	if (fetching && !userDetails) return <Loading />;
	const context = getLaunchDarklyContext(userDetails);
	// Do not render a new account with the previous account's flags, even for one frame.
	const identity = JSON.stringify([
		userDetails?.property_manager_id,
		userDetails?.partner?.partner_id,
	]);
	return (
		<LaunchDarklyProvider key={identity} context={context}>
			{children}
		</LaunchDarklyProvider>
	);
}
